import { UntypedFormGroup } from "@angular/forms";
import { WorkforcePlanTeamEmployeeModel, WorkforcePlanWorkScaleGroupModel } from "services/models/workforceplan";
import { WorkforcePlanSolicitationDetailsModel } from "services/models/workforceplan/workforceplan-solicitation-details.model";
import { WorkforcePlanSolicitationModel } from "services/models/workforceplan/workforceplan-solicitation.model";

/**
 * Contract for solicitation form components used by the workforce plan drawer.
 * Each drawer type (e.g. cost center change, training) can have its own form component
 * implementing this interface so the drawer can validate and obtain payload uniformly.
 * Implementors should accept @Input() data and optionally @Input() source ('action-required' | null)
 * from the parent drawer.
 */
export interface ISolicitationFormComponent {
	getForm(): UntypedFormGroup;
	/** Payload for create/update movement request; must include id when in details mode */
	getPayload(): { id?: string;[key: string]: unknown };
	/** JSON string for approval/rejection payload when this type supports it; undefined otherwise */
	getUpdatePayload?(): string;
}

export type DrawerMode = "add" | "details";

export type DrawerStatus = "loading" | "fetched" | "error";

export type DrawerType =
	| 'SHIFT_CHANGE'
	// | 'JOBTITLE_CHANGE'
	| 'REQUEST_TRAINING'
	| 'REQUEST_APTITUDE_TRAINING'
	| 'EMPLOYEE_DETAILS'
	| 'COST_CENTER_AND_JOB_TITLE_CHANGE'
	| 'REQUEST_DISMISSAL'
	| 'REQUEST_HIRING_INCREASE'
	| 'REQUEST_HIRING_SUBSTITUTION'
	| 'REQUEST_HIRING_REPLACEMENT'
	| 'REQUEST_PROMOTION'
	| 'SOLICITATION_DETAILS'
	| 'REQUEST_WORK_FORCEPLAN'
	| 'REQUEST_DISCIPLINARY_MEASURE'
	| 'REQUEST_FEEDBACK';



/**
 * Input data for the workforce plan solicitation drawer.
 * Note: employee may be partial (e.g. only id when opening from details/action-required).
 * Form components that need full employee data should load it when partial (e.g. CostCenterChangeFormComponent).
 */
export type DrawerDataInput = {
	mode: DrawerMode;
	type: DrawerType;
	employee: WorkforcePlanTeamEmployeeModel;
	companyId: string;
	drawerOpened: boolean;
	solicitation?: WorkforcePlanSolicitationModel
	solicitationDetails?: WorkforcePlanSolicitationDetailsModel
	workScaleGroup?: string;
	workforcePlanId: string;
	workScaleGroups: WorkforcePlanWorkScaleGroupModel[];
	costCenterId?: string;
	costCenterName?: string;
	replacedEmployeeId?: string;
	hasSubType?: boolean;
}

export type NewRecruitmentEmployee = {
	id: string;
	costCenterId: string;
	costCenterName: string;
	jobTitleId: string;
	jobTitleName: string;
	jobTitleGroupId: string;
	jobTitleGroupName: string;
	name: string;
	profileImagePath: string;
	shortProfileImagePath: string;
	registration: string;
	resignationDate: string;
	timeshiftId: string;
	timeshiftTitle: string;
	status: string;
}

export type FormCostCenterValues = {
	note: string;
	employeeId: string;
	movementType: string;
	workforcePlanId: string;
	workScaleGroup: string;
	currentCostCenterId: string;
	currentCostCenterName: string;
	currentTimeshiftId: string;
	currentTimeshiftName: string;
	currentJobTitleId: string;
	currentJobTitleName: string;
	newCostCenterId: string;
	newCostCenterName: string;
	newTimeshiftId: string;
	newTimeshiftName: string;
	newJobTitleId: string;
	newJobTitleName: string;
	newJobTitleGroupId: string;
	newJobTitleGroupName: string;
	changeTypeCode: string;
	hasDangerousness: boolean;
	unhealthinessType: string;
	newSalary?: number | null;
	tradeUnionId: string;
	executionDate: string;
	salaryRange?: MovementSalaryRange | null;
}

export type FormCostCenterPayload = {
	id?: string;
	note: string;
	employeeId: string;
	movementType: string;
	workforcePlanId: string;
	workScaleGroup: string;
	payload: string;
}

export type RequestTrainingValues = {
	movementType: string;
	workforcePlanId: string;
	workScaleGroup: string;
	employeeId: string;
	effectiveDate: string;
	note: string;
	trainingTypeId: string;
	trainingTypeName: string;
	jobTitleId: string;
	jobTitleName: string;
	jobTitleGroupId: string;
	jobTitleGroupName: string;
	costCenterId: string;
	costCenterName: string;
	startTrainingDate: string;
	endTrainingDate: string;
	isApproved?: boolean;
	newSalary?: number | null;
	tradeUnionId: string;
	tradeUnionName: string;
	changeTypeCode: string;
	hasDangerousness: boolean;
	unhealthinessType: string;
	executionDate?: string;
	salaryRange?: MovementSalaryRange | null;
}

export type RequestTrainingPayload = {
	id?: string;
	movementType: string;
	workforcePlanId: string;
	workScaleGroup: string;
	employeeId: string;
	effectiveDate: string;
	note: string;
	payload: string;
}

export type RequestAptitudeTrainingValues = {
	movementType: string;
	workforcePlanId: string;
	workScaleGroup: string;
	employeeId: string;
	effectiveDate: string;
	note: string;
	trainingTypeId: string;
	trainingTypeName: string;
	jobTitleId: string;
	jobTitleName: string;
	jobTitleGroupId: string;
	jobTitleGroupName: string;
	costCenterId: string;
	costCenterName: string;
	startTrainingDate: string;
	endTrainingDate: string;
	isApproved?: boolean;
}

export type RequestAptitudeTrainingPayload = {
	id?: string;
	movementType: string;
	workforcePlanId: string;
	workScaleGroup: string;
	employeeId: string;
	effectiveDate: string;
	note: string;
	payload: string;
}

// export type FormJobTitleChangeValues = {
// 	note: string;
// 	employeeId: string;
// 	movementType: string;
// 	workforcePlanId: string;
// 	workScaleGroup: string;
// 	currentJobTitleId: string;
// 	currentJobTitleName: string;
// 	newJobTitleId: string;
// 	newJobTitleName: string;
// }

// export type FormJobTitleChangePayload = {
// 	id?: string;
// 	note: string;
// 	employeeId: string;
// 	movementType: string;
// 	workforcePlanId: string;
// 	workScaleGroup: string;
// 	payload: string;
// }

export type FormRequestDismissalValues = {
	note: string;
	employeeId: string;
	movementType: string;
	effectiveDate: string;
	workforcePlanId: string;
	workScaleGroup: string;
	resignationReason: string;
	dismissalType: number;
	wasEmployeeInitiative: string;
	vacancyReplacement: boolean;
	expectedDate: string;
}

export type FormRequestDismissalPayload = {
	id?: string;
	note: string;
	employeeId: string;
	movementType: string;
	effectiveDate?: string;
	workforcePlanId: string;
	workScaleGroup: string;
	payload: string;
}

export type FormRequestHiringIncreaseValues = {
	movementType: string;
	workforcePlanId: string;
	workScaleGroup: string;
	jobTitleId: string;
	jobTitleName: string;
	jobTitleGroupId: string;
	timeShiftId: string;
	timeShiftName: string;
	raiseType: string;
	note: string;
	amount: string;
}

export type FormRequestHiringIncreasePayload = {
	id?: string;
	note: string;
	employeeId: string;
	movementType: string;
	workScaleGroup: string;
	workforcePlanId: string;
	payload: string;
}

export type FormRequestHiringSubstitutionValues = {
	movementType: string;
	workforcePlanId: string;
	workScaleGroup: string;
	employeeId: string;
	note: string;
	timeShiftId: string;
	timeShiftName: string;
	jobTitleId: string;
	jobTitleName: string;
	raiseType: string;
	deadline: string;
}

export type FormRequestHiringSubstitutionPayload = {
	id?: string;
	note: string;
	employeeId: string;
	movementType: string;
	workScaleGroup: string;
	workforcePlanId: string;
	payload: string;
}

export type SolicitationUpdateStatusData = {
	movementRequestId: string;
	requesterId: string;
	approverId: string;
	approvalScopeItemId: string;
	status: number;
	effectiveDate: string;
	note: string;
	movementRequestNote: string;
	payload: string;
	customFields?: SolicitationCustomFieldValue[];
}

export type SolicitationCustomFieldValue = {
	customFieldId: string;
	key: string;
	label: string;
	value: string;
}

export type FormRequestDisciplinaryMeasure = {
	note: string;
	movementType: string;
	workforcePlanId: string;
	employeeId: string;
	workScaleGroup: string;
	occurenceDate: string;
	amountDays: number;
	occurenceType: string;
	occurrenceJustification: string;
}

export type FormRequestDisciplinaryMeasurePayload = {
	id?: string;
	note: string;
	movementType: string;
	workforcePlanId: string;
	effectiveDate: string;
	employeeId: string;
	workScaleGroup: string;
	occurenceDate: string;
	payload: string;
}


export type FormFeedback = {
	note: string;
	movementType: string;
	workforcePlanId: string;
	employeeId: string;
	workScaleGroup: string;
	occurenceDate: string;
	amountDays: number;
	occurenceType: string;
	occurrenceJustification: string;
}

export type FormFeedbackPayload = {
	id?: string;
	note: string;
	movementType: string;
	workforcePlanId: string;
	effectiveDate: string;
	employeeId: string;
	workScaleGroup: string;
	occurenceDate: string;
	payload: string;
}

export enum MovementStatusType {
	NONE = 0,
	PENDING = 1,
	APPROVED = 2,
	REJECTED = 3,
	CANCELED = 4
}

export type FormRequestHiringReplacementValues = {
	movementType: string;
	workforcePlanId: string;
	workScaleGroup: string;
	jobTitleId: string;
	jobTitleName: string;
	jobTitleGroupId: string;
	jobTitleGroupName: string;
	timeShiftId: string;
	timeShiftName: string;
	raiseType: string;
	note: string;
}

export type FormRequestHiringReplacementPayload = {
	id?: string;
	note: string;
	movementType: string;
	workScaleGroup: string;
	workforcePlanId: string;
	payload: string;
}

export type FormRequestWorkforcePlanValues = {
	note: string;
	movementType: string;
	workforcePlanId: string;
}

export type FormRequestWorkforcePlanPayload = {
	id?: string;
	note: string;
	movementType: string;
	workforcePlanId: string;
}

export type FormRequestPromotionValues = {
	movementType: string;
	workforcePlanId: string;
	workScaleGroup: string;
	employeeId: string;
	effectiveDate: string;
	note: string;
	promotionType: string;
	changeTypeCode: string;
	newSalary?: number | null;
	jobTitleGroupId: string;
	jobTitleGroupName: string;
	jobTitleId: string;
	jobTitleName: string;
	currentJobTitleId: string;
	currentJobTitleName: string;
	executionDate?: string;
	salaryRange?: MovementSalaryRange | null;
	hasDangerousness?: boolean | null;
	unhealthinessType?: string | null;
};

export type FormRequestPromotionPayload = {
	id?: string;
	note: string;
	employeeId: string;
	movementType: string;
	workScaleGroup: string;
	workforcePlanId: string;
	effectiveDate?: string;
	payload: string;
};

export type MovementSalaryRange = {
	salaryScaleId: string;
	salaryRangeId: string;
	code: string;
	amount: number;
};
